import { saveAuthReturnTo } from "../hooks/useAuth0DeepLink.js";
import { IS_IOS_APP } from "../config/appTarget.js";

/** Window event that opens the native iOS login sheet. */
export const IOS_LOGIN_EVENT = "pawseum:ios-login";

/**
 * Trigger an Auth0 login redirect and restore the originating path afterwards
 * on BOTH web and native builds.
 *
 * Why this helper exists
 * ─────────────────────
 * There are two separate return-to mechanisms that must both be populated:
 *
 *  Web:    Auth0 preserves `appState.returnTo` through the OAuth redirect and
 *          delivers it to `onRedirectCallback` in Auth0Provider.tsx.
 *
 *  Native: The app goes into the background while Chrome Custom Tabs /
 *          SFSafariViewController opens. When the Auth0 callback fires the
 *          `appUrlOpen` event, `useAuth0DeepLink` reads AUTH_RETURN_TO from
 *          @capacitor/preferences — so `saveAuthReturnTo` MUST be called
 *          before the browser sheet opens, otherwise the user lands on '/'.
 *
 * Calling `saveAuthReturnTo` is safe on web too (it uses an in-memory Map
 * and doesn't cost anything), so this helper always calls both.
 */
export function loginWithReturn(
  loginWithRedirect: (opts?: Record<string, unknown>) => Promise<void>,
  returnTo: string = window.location.pathname,
  extra?: Record<string, unknown>,
): void {
  // Write to appStorage *before* opening the browser (native) or redirecting (web).
  // Not awaited intentionally — Preferences.set is fast and loginWithRedirect
  // is asynchronous, so the write completes well before the callback fires.
  saveAuthReturnTo(returnTo);

  if (IS_IOS_APP) {
    window.dispatchEvent(
      new CustomEvent(IOS_LOGIN_EVENT, { detail: { returnTo } }),
    );
    return;
  }

  // Open Auth0 Universal Login. On native shells the Auth0Provider `openUrl`
  // override presents it in SFSafariViewController / Chrome Custom Tabs, so the
  // hosted page (email/password, Google, Facebook, Apple) opens in-app without
  // jumping to the default browser.
  void loginWithRedirect({ ...extra, appState: { returnTo } });
}
